import html2canvas from "html2canvas";
import { create } from "zustand";
import * as SharedChatResponse from "../../darvin-shared/schema/zod/agent/chat-response.schema";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { useProjectStore } from "../core_stores/useProjectStore";
import { debugLog } from "../utils/logUtils";

export enum PreviewMode {
  Desktop = "desktop",
  MobilePortrait = "mobilePortrait",
  MobileLandscape = "mobileLandscape",
  Tablet = "tablet",
}

export enum PreviewUrlStatus {
  Ready = "ready",
  Updating = "updating",
  NotAvailable = "notAvailable",
  Error = "error",
}

interface ProjectPreviewActions {
  initialize: () => void;
  setIframeRef: (iframeRef: React.RefObject<HTMLIFrameElement>) => void;
  setPreviewMode: (previewMode: PreviewMode) => void;
  increaseScale: () => void;
  decreaseScale: () => void;
  resetScale: () => void;
  generateScreenshot: () => Promise<HTMLCanvasElement | undefined>;
  reload: () => void;
  togglePause: () => void;
  dispose: () => void;
}

interface ProjectPreviewState {
  url: string | null;
  version: string | null;
  mode: PreviewMode;
  scale: number;
  isPaused: boolean;
  status: PreviewUrlStatus;

  actions: ProjectPreviewActions;
}

function isPreviewUrlMessage(msg: SharedChatResponse.MessageBody) {
  return msg.type === SharedMessageTypes.MessageTypeEnum.enum.previewUrl;
}

function getPreviewUrlMessageContent(
  msg: SharedChatResponse.MessageBody
): SharedMessageTypes.PreviewUrl | undefined {
  if (!isPreviewUrlMessage(msg)) {
    return undefined;
  }
  return msg.content as SharedMessageTypes.PreviewUrl;
}

function mapPreviewUrlStatus(
  status: SharedMessageTypes.PreviewUrlStatus
): PreviewUrlStatus {
  switch (status) {
    case SharedMessageTypes.PreviewUrlStatusEnum.enum.success:
      return PreviewUrlStatus.Ready;
    case SharedMessageTypes.PreviewUrlStatusEnum.enum.updating:
      return PreviewUrlStatus.Updating;
    case SharedMessageTypes.PreviewUrlStatusEnum.enum.notAvailable:
      return PreviewUrlStatus.NotAvailable;
    case SharedMessageTypes.PreviewUrlStatusEnum.enum.error:
      return PreviewUrlStatus.Error;
    default:
      return PreviewUrlStatus.NotAvailable;
  }
}

const useProjectPreviewStore = create<ProjectPreviewState>()((set, get) => {
  let unsubscribeProjectHistory: (() => void) | null = null;
  let unsubscribeLatestMsg: (() => void) | null = null;
  let iframeRef: React.RefObject<HTMLIFrameElement> | null = null;

  function handlePreviewUrl(
    latestEvent: SharedChatResponse.MessageBody | undefined,
    isReload: boolean
  ) {
    const projectId = useProjectStore.getState().projectId;
    if (!latestEvent || !projectId) {
      debugLog(
        `[DEBUG ${new Date().toISOString()}] PreviewStore - skipping update (no event or projectId)`
      );
      return;
    }
    const webPreviewInfo = getPreviewUrlMessageContent(latestEvent);
    if (!webPreviewInfo) {
      throw new Error("Latest event is not a preview url message");
    }

    // DEBUG: Track when handlePreviewUrl is called
    debugLog(
      `[DEBUG ${new Date().toISOString()}] PreviewStore handlePreviewUrl - latestEvent:`,
      latestEvent,
      "projectId:",
      projectId,
      "currentUrl:",
      get().url
    );

    if (
      webPreviewInfo.status ===
        SharedMessageTypes.PreviewUrlStatusEnum.enum.notAvailable ||
      !webPreviewInfo.url
    ) {
      debugLog(
        `[DEBUG ${new Date().toISOString()}] PreviewStore - clearing URL due to not available status`
      );
      set({
        url: null,
        status: PreviewUrlStatus.NotAvailable,
        version: null,
      });
      return;
    }

    const { origin } = window.location;
    const finalUrl = `${origin}${webPreviewInfo.url}`;
    if (finalUrl !== get().url) {
      debugLog(
        `[DEBUG ${new Date().toISOString()}] PreviewStore - setting new URL:`,
        finalUrl
      );
    }
    if (webPreviewInfo.status !== get().status) {
      debugLog(
        `[DEBUG ${new Date().toISOString()}] PreviewStore - status changed:`,
        webPreviewInfo.status
      );
    }

    set((state) => ({
      url: finalUrl,
      status: mapPreviewUrlStatus(webPreviewInfo.status),
      version:
        isReload || // During reload, force update version to be in sync with the history
        webPreviewInfo.status ===
          SharedMessageTypes.PreviewUrlStatusEnum.enum.success
          ? latestEvent.messageId
          : state.version,
    }));
  }

  return {
    url: null,
    version: null,
    mode: PreviewMode.MobilePortrait,
    scale: 1,
    isPaused: false,
    status: PreviewUrlStatus.NotAvailable,

    actions: {
      initialize: () => {
        unsubscribeProjectHistory = useProjectStore.subscribe(
          (state) => state.history,
          (history) => {
            if (history && history.length > 0) {
              const latestPreviewUrl = [...history]
                .reverse()
                .find(
                  (msg) =>
                    msg.type ===
                    SharedMessageTypes.MessageTypeEnum.enum.previewUrl
                );
              handlePreviewUrl(latestPreviewUrl, true);
            }
          }
        );
        unsubscribeLatestMsg = useProjectStore.subscribe(
          (state) => state.latestMsg,
          (latestMsg) => {
            if (
              latestMsg?.type ===
              SharedMessageTypes.MessageTypeEnum.enum.previewUrl
            ) {
              handlePreviewUrl(latestMsg, false);
            }
          }
        );
      },

      setIframeRef: (ref: React.RefObject<HTMLIFrameElement>) => {
        iframeRef = ref;
      },

      setPreviewMode: (previewMode: PreviewMode) => set({ mode: previewMode }),

      increaseScale: () =>
        set((state) => ({
          scale: Math.max(0.5, Math.min(2, state.scale + 0.1)),
        })),

      decreaseScale: () =>
        set((state) => ({
          scale: Math.max(0.5, Math.min(2, state.scale - 0.1)),
        })),

      resetScale: () => set({ scale: 1 }),

      generateScreenshot: async () => {
        if (!iframeRef) return;

        console.log("iframeRef::", iframeRef);

        const iframeBody = iframeRef!.current?.contentDocument?.body;
        if (!iframeBody) return;

        console.log("iframeBody::", iframeBody);

        return await html2canvas(iframeBody, {
          background: "#ffffff",
        });
      },

      reload: () => {
        set((state) => ({
          version: state.version ? `${state.version}-${Date.now()}` : null,
        }));
      },

      togglePause: () => set((state) => ({ isPaused: !state.isPaused })),

      dispose: () => {
        // DEBUG: Track when preview store is disposed
        debugLog(
          `[DEBUG ${new Date().toISOString()}] PreviewStore - dispose called`
        );
        if (unsubscribeProjectHistory) {
          unsubscribeProjectHistory();
          unsubscribeProjectHistory = null;
        }
        if (unsubscribeLatestMsg) {
          unsubscribeLatestMsg();
          unsubscribeLatestMsg = null;
        }
        set({
          url: null,
          version: null,
          mode: PreviewMode.MobilePortrait,
          status: PreviewUrlStatus.NotAvailable,
          scale: 1,
          isPaused: false,
        });
      },
    },
  };
});

export const useProjectPreviewUrl = () =>
  useProjectPreviewStore((state) => state.url);
export const useProjectPreviewStatus = () =>
  useProjectPreviewStore((state) => state.status);
export const useProjectPreviewVersion = () =>
  useProjectPreviewStore((state) => state.version);
export const useProjectPreviewMode = () =>
  useProjectPreviewStore((state) => state.mode);
export const useProjectPreviewScale = () =>
  useProjectPreviewStore((state) => state.scale);
export const useProjectPreviewIsPaused = () =>
  useProjectPreviewStore((state) => state.isPaused);
export const useProjectPreviewActions = () =>
  useProjectPreviewStore((state) => state.actions);
